import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Entity } from "@common/types/entity";
import { getEntitiesAccountsQueryKey } from "@queries/entitiesAccounts";
import { fetchEntitiesAccounts } from "@queries/fetchEntitiesAccounts";

/**
 * Entities of any workspace, independent of the active scope. Shares the query
 * key, fetcher and mount behavior with `useEntitiesContexts`, so the table
 * rendered under a sub-scope for the same workspace doesn't refetch them.
 */
export const useWorkspaceEntities = (
    workspaceId: string | null,
): { entities: Entity[]; isLoading: boolean } => {
    const { data, isLoading } = useQuery({
        queryKey: getEntitiesAccountsQueryKey(workspaceId ?? ""),
        queryFn: async () => await fetchEntitiesAccounts(workspaceId),
        refetchOnMount: false,
        enabled: !!workspaceId,
    });

    const entities = useMemo(
        () =>
            data?.entities
                .map((entityContext) => entityContext.entity)
                .filter((entity) => !entity.isMockEntity) ?? [],
        [data],
    );

    return { entities, isLoading };
};
